import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { debounce, deepEqual } from '../01-core/Utilities';
import { Checkbox } from './Checkbox';
import { Radio } from './Radio';
import { Input } from './Input';
import { Button } from './Button';
import { Action } from './Action';
import { AnimatedDetails } from '../01-core/AnimatedDetails';

export type DataMap = Record<string, string | string[] | null | undefined>;

/** Ensure a value is an array (handles single string values from URL params) */
const ensureArray = (value: string | string[] | null | undefined): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

export interface DateRangeLabel {
  from: string;
  to: string;
}

export type DropdownItem = {
  type?: 'checkbox' | 'radio' | 'daterange';
  label: DateRangeLabel | string;
  name?: string;
  value?: { from?: string; to?: string } | string;
  checked?: boolean;
  selectall?: boolean;
  values?: DropdownItem[];
};

export interface DropdownProps {
  /** Tag label */
  label: string;
  /** Title on tag */
  title?: string;
  /** Key */
  key?: string | number;
  /** Name */
  name?: string;
  /** List of value groups */
  values?: DropdownItem[];
  /** The current data */
  data?: DataMap | null;
  /** The data of the currently applied filter — the baseline the selection is compared against */
  appliedData?: DataMap | null;
  /** Show active count */
  showActiveCount?: boolean;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Search label */
  search?: string;
  /** Search results label */
  searchResults?: string;
  /** Search selected label */
  searchSelected?: string;
  /** Search no results message */
  searchNoResults?: string;
  /** Search index */
  searchIndex?: string;
  /** Show more label */
  showMore?: string;
  /** Search handler */
  onSearch?: (
    term: string,
  ) => Promise<{ label: string; value: string; name: string }[]>;
  /** Get search term by value */
  getSearchTerm?: (
    value: string,
  ) => Promise<{ label: string; value: string; name: string } | null>;
  /** Apply label */
  apply?: string;
  /** Close label, shown instead of the apply label while the selection is unchanged */
  close?: string;
  /** Optional apply handler */
  onApply?: (data: DataMap) => void;
  /** Optional handler called whenever the dropdown is closed via its action button or Escape */
  onClose?: () => void;
  /** Reset label */
  reset?: string;
  /** Optional reset handler */
  onReset?: (data: DataMap) => void;
  /** On change event handler */
  onChange?: (data: DataMap) => void | null;
  /** Additional class */
  className?: string;
  /** Select all label */
  selectAll?: string;
  /** Render the options inline (no details popover) — used in the mobile filter drawer */
  inline?: boolean;
}

interface DropdownValueProps {
  item: DropdownItem;
  data?: DataMap | null;
  searchTerm?: string;
  disabled?: boolean;
  onChange?: (data: DataMap) => void;
  group?: DropdownItem | null;
}

interface DropdownValuesProps {
  values?: DropdownItem[];
  data?: DataMap | null;
  searchTerm?: string;
  disabled?: boolean;
  onChange?: (data: DataMap) => void;
  group?: DropdownItem | null;
}

const isGroup = (i: DropdownItem) => 'values' in i;
const isCheckbox = (i: DropdownItem) => i?.type === 'checkbox';
const isRadio = (i: DropdownItem) => i?.type === 'radio';
const isDateRange = (i: DropdownItem) => i?.type === 'daterange';
const toDateInputValue = (v?: string | null) => {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const DropdownValue = ({
  item,
  data,
  searchTerm,
  disabled,
  onChange,
  group,
}: DropdownValueProps) => {
  const onSelectAll = (checked: boolean) => {
    if (onChange && isGroup(item)) {
      const update: DataMap = {};
      item?.values?.forEach((child) => {
        if (isCheckbox(child)) {
          if (!update[child.name as string]) update[child.name as string] = [];
          if (checked) {
            (update[child.name as string] as string[]).push(
              child.value as string,
            );
          }
        }
      });
      onChange(update);
    }
  };

  if (!group && isGroup(item)) {
    let allSelected = true;
    if (item.selectall && item?.values?.length) {
      for (let k = 0; k < item.values.length; k++) {
        const child = item.values[k];
        if (
          isCheckbox(child) &&
          (!data?.[child.name as string] ||
            !(data?.[child.name as string] as string[])?.includes(
              child.value as string,
            ))
        ) {
          allSelected = false;
          break;
        }
      }
    }

    return (
      <>
        {item.selectall ? (
          <Checkbox
            label={item.label as string}
            name=""
            value=""
            disabled={disabled}
            small={true}
            checked={allSelected}
            onChange={onSelectAll}
          />
        ) : isCheckbox(item) ? (
          <Checkbox
            label={item.label as string}
            name={item.name}
            value={item.value as string}
            disabled={disabled}
            small={true}
            checked={ensureArray(data?.[item.name as string]).includes(
              item.value as string,
            )}
            onChange={(checked: boolean) => {
              if (onChange) {
                const update: DataMap = {};
                const current = ensureArray(data?.[item.name as string]);
                const next = checked
                  ? [...current, item.value]
                  : current.filter((v) => v !== (item.value as string));
                update[item.name as string] = next as string[];
                onChange(update);
              }
            }}
          />
        ) : (
          <span className="dropdown__group-label">{item.label as string}</span>
        )}
        <ul className="dropdown__values">
          <DropdownValues
            values={item.values}
            data={data}
            searchTerm={searchTerm}
            disabled={disabled}
            onChange={onChange}
            group={item}
          />
        </ul>
      </>
    );
  }

  if (isCheckbox(item)) {
    return (
      <Checkbox
        label={item.label as string}
        name={item.name}
        value={item.value as string}
        disabled={disabled}
        small={true}
        checked={ensureArray(data?.[item.name as string]).includes(
          item.value as string,
        )}
        onChange={(checked: boolean) => {
          if (onChange) {
            const update: DataMap = {};
            const current = ensureArray(data?.[item.name as string]);
            const next = checked
              ? [...current, item.value]
              : current.filter((v) => v !== item.value);
            update[item.name as string] = next as string[];
            onChange(update);
          }
        }}
      />
    );
  }

  if (isRadio(item)) {
    return (
      <Radio
        label={item.label as string}
        name={item.name}
        value={item.value as string}
        disabled={disabled}
        small={true}
        selected={data?.[item.name as string] === (item.value as string)}
        onChange={(selected: boolean) => {
          if (selected) {
            const update: DataMap = {};
            update[item.name as string] = item.value as string;
            if (onChange) onChange(update);
          }
        }}
      />
    );
  }

  if (isDateRange(item)) {
    const onInputChange = (key: 'from' | 'to', value: string) => {
      if (onChange) {
        const update: DataMap = {};
        update[`${item.name}_${key}`] = value;
        onChange(update);
      }
    };

    const fromVal = toDateInputValue(data?.[`${item.name}_from`] as string);
    const toVal = toDateInputValue(data?.[`${item.name}_to`] as string);

    return (
      <>
        <label className="dropdown__value-label">
          {(item?.label as DateRangeLabel)?.from}
          <Input
            type="date"
            name={`${item.name}_from`}
            value={fromVal}
            disabled={disabled}
            placeholder="From"
            max={toVal || undefined}
            onChange={(v: string | React.ChangeEvent<HTMLInputElement>) => {
              const value = typeof v === 'string' ? v : v.target.value;
              onInputChange('from', value);
            }}
            autoComplete="off"
          />
        </label>

        <label className="dropdown__value-label">
          {(item?.label as DateRangeLabel)?.to}
          <Input
            type="date"
            name={`${item.name}_to`}
            value={toVal}
            disabled={disabled}
            placeholder="To"
            min={fromVal || undefined}
            onChange={(v: string | React.ChangeEvent<HTMLInputElement>) => {
              const value = typeof v === 'string' ? v : v.target.value;
              onInputChange('to', value);
            }}
            autoComplete="off"
          />
        </label>
      </>
    );
  }

  return null;
};

const DropdownValues = ({
  values = [],
  data,
  searchTerm,
  disabled,
  onChange,
  group,
}: DropdownValuesProps) => {
  return (
    <>
      {values.map((item, i) => {
        let search = searchTerm || '';
        let hidden = !!searchTerm;
        const labelStr =
          typeof (item as any).label === 'string'
            ? String((item as any).label)
            : '';
        if (search) {
          hidden = labelStr.toLowerCase().indexOf(search) === -1;
          if (isGroup(item) && item.values) {
            if (hidden) {
              for (const k in item.values) {
                const child = item.values[k] as any;
                const childLabel =
                  typeof child.label === 'string' ? child.label : '';
                if (childLabel.toLowerCase().indexOf(search) > -1) {
                  hidden = false;
                  break;
                }
              }
            } else {
              search = '';
            }
          }
        }

        return (
          <li
            key={i}
            className={
              (group
                ? 'dropdown__value dropdown__value--' +
                  ((item as any).type || 'group')
                : 'dropdown__group') +
              (hidden ? ' dropdown__value--hidden' : '')
            }
          >
            <DropdownValue
              data={data || undefined}
              searchTerm={search}
              item={item}
              disabled={disabled}
              onChange={onChange || undefined}
              group={group || undefined}
            />
          </li>
        );
      })}
    </>
  );
};

/** A dropdown as multi select replacement */
export const Dropdown = ({
  label,
  title,
  name,
  values = [],
  data = null,
  appliedData = null,
  showActiveCount = true,
  disabled,
  search,
  searchSelected,
  searchResults,
  searchNoResults,
  searchIndex,
  showMore,
  onSearch,
  getSearchTerm,
  apply,
  close,
  onApply,
  onClose,
  reset,
  onReset,
  onChange,
  className,
  inline,
}: DropdownProps) => {
  const ref = useRef<HTMLDetailsElement | null>(null);
  const wrapper = useRef<HTMLDivElement | null>(null);
  const content = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const scrollContainer = useRef<HTMLUListElement | null>(null);
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [selectedSearchValues, setSelectedSearchValues] = useState<
    DropdownItem[]
  >([]);
  const [searchValues, setSearchValues] = useState<DropdownItem[]>([]);
  const [noResults, setNoResults] = useState<boolean>(false);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [dataState, setData] = useState<DataMap | null>(data);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [doShowMore, setShowMore] = useState<boolean>(false);

  const applyIfDirtyRef = useRef<() => void>(() => {});
  const isOpenRef = useRef<boolean>(false);

  const prevDataRef = useRef(data);
  useEffect(() => {
    if (!deepEqual(data, prevDataRef.current)) {
      prevDataRef.current = data;
      setData(data ?? null);
      if (getSearchTerm && searchIndex) {
        const values = [...searchValues];
        let selected = [...selectedSearchValues];
        let changed = false;
        if (selectedSearchValues?.length) {
          selectedSearchValues.forEach((item) => {
            if (
              data?.[searchIndex] &&
              (data[searchIndex] as string[]).includes(item.value as string)
            )
              return;
            changed = true;
            values.push(item);
            selected = selected.filter((i) => i !== item);
          });
        }
        if (changed) {
          setSearchValues(values);
          setSelectedSearchValues(selected);
        }
        if (data?.[searchIndex]) {
          let value = data[searchIndex];
          if (!Array.isArray(value)) {
            value = [value];
          }
          value.forEach((val) => {
            if (selectedSearchValues.find((item) => item.value === val)) return;
            getSearchTerm(val).then((item) => {
              if (item === null) return;
              selected.push({
                type: 'checkbox',
                ...item,
              });
              setSelectedSearchValues(selected);
            });
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const maybeReposition = useCallback(() => {
    if (!wrapper.current || !content.current || !ref.current) return;
    wrapper.current.removeAttribute('style');
    wrapper.current.style.setProperty('--max-height', '0');
    const rect = content.current.getBoundingClientRect();
    wrapper.current.style.removeProperty('--max-height');
    if (rect.left + rect.width > window.innerWidth) {
      wrapper.current.style.left =
        -(rect.left + rect.width + 20 - window.innerWidth) + 'px';
    }

    const body = document.getElementsByTagName('body')[0] as HTMLBodyElement;
    const docHeight = body.offsetHeight;
    if (rect.top - window.scrollY + rect.height > docHeight) {
      const parent_rect = ref.current.getBoundingClientRect();
      if (rect.height < parent_rect.top + window.scrollY) {
        wrapper.current.style.top = 'auto';
        wrapper.current.style.bottom = '100%';
      } else {
        wrapper.current.style.setProperty(
          '--max-height',
          docHeight - rect.top - 10 + 'px',
        );
      }
    }
  }, [wrapper, content, ref]);

  const handleResize: () => void = useCallback(() => {
    debounce(
      () => {
        maybeReposition();
      },
      100,
      false,
    )();
  }, [maybeReposition]);

  const maybeClose: (e: Event) => void = useCallback(
    (e) => {
      if (
        isOpenRef.current &&
        ref.current &&
        e.target instanceof Node &&
        !ref.current.contains(e.target as Node) &&
        document.contains(e.target as Node)
      ) {
        setOpen(false);
        applyIfDirtyRef.current();
      }
    },
    [ref],
  );

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.requestAnimationFrame(() => {
      maybeReposition();

      if (scrollContainer.current) {
        scrollContainer.current.scrollTop = scrollPosition;
      }
    });
    window.addEventListener('scroll', maybeReposition, {
      passive: true,
    } as AddEventListenerOptions);
    window.addEventListener('click', maybeClose);
    window.addEventListener('focus', maybeClose);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', maybeReposition);
      window.removeEventListener('click', maybeClose);
      window.removeEventListener('focus', maybeClose);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollContainer, handleResize, maybeReposition, maybeClose]);

  const emptyData = useMemo(() => {
    const emptyData: DataMap = {};
    if (searchIndex) {
      emptyData[searchIndex] = [];
    }
    // Recursively collect all checkbox/radio/daterange names from nested structure
    const stack = [...values];
    while (stack.length) {
      const item = stack.shift();
      if (!item) continue;
      // Push children to stack for processing
      if (item.values?.length) {
        stack.push(...item.values);
      }
      // Process the item itself if it has a name
      if (!item.name) continue;
      if (isDateRange(item)) {
        emptyData[item.name + '_from'] = null;
        emptyData[item.name + '_to'] = null;
      } else if (isCheckbox(item) || isRadio(item)) {
        emptyData[item.name] = isCheckbox(item) ? [] : null;
      }
    }
    return emptyData;
  }, [values, searchIndex]);

  useEffect(() => {
    let count = 0;
    const stack = [...values];
    while (stack?.length) {
      const item = stack.shift() as DropdownItem;
      if (isGroup(item)) {
        stack.push(...(item.values || []));
      }
      if (
        item?.name &&
        ((isRadio(item) && item.value === (dataState?.[item.name] as string)) ||
          (isCheckbox(item) &&
            ensureArray(dataState?.[item.name]).includes(item.value as string)))
      ) {
        count++;
      }
      if (isDateRange(item)) {
        if (
          dataState?.[item.name + '_from'] ||
          dataState?.[item.name + '_to']
        ) {
          count++;
        }
      }
    }

    const total = count + selectedSearchValues.length;
    if (selectedCount !== total) setSelectedCount(total);
    if (ref.current && showActiveCount) {
      ref.current.classList.toggle('dropdown--active', total > 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, dataState, selectedSearchValues.length, showActiveCount]);
  const narrowToOwnKeys = useCallback(
    (source: DataMap | null) => {
      const narrowed: DataMap = {};
      Object.keys(emptyData).forEach((key) => {
        const value = source?.[key];
        narrowed[key] = Array.isArray(emptyData[key])
          ? [...ensureArray(value)].sort()
          : value ?? null;
      });
      return narrowed;
    },
    [emptyData],
  );

  const isDirty = !deepEqual(
    narrowToOwnKeys(dataState),
    narrowToOwnKeys(appliedData ?? data),
  );

  const applyIfDirty = useCallback(() => {
    if (!isDirty || !onApply || !dataState) return;

    const applyData: DataMap = {};
    Object.keys(emptyData).forEach((key) => {
      applyData[key] = dataState[key];
    });
    onApply(applyData);
  }, [isDirty, onApply, dataState, emptyData]);

  useEffect(() => {
    applyIfDirtyRef.current = applyIfDirty;
  }, [applyIfDirty]);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const actionLabel = isDirty ? apply || close : close || apply;

  const focusLabel = () => {
    ref.current?.querySelector('summary')?.focus();
  };

  const handleApply = () => {
    setOpen(false);
    applyIfDirtyRef.current();
    focusLabel();
    onClose?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDetailsElement>) => {
    if (e.key !== 'Escape' || !isOpen) return;

    e.stopPropagation();
    setOpen(false);
    applyIfDirtyRef.current();
    focusLabel();
    onClose?.();
  };

  const handleReset = () => {
    setSearchValues([...selectedSearchValues, ...searchValues]);
    setSelectedSearchValues([]);
    setOpen(false);

    if (onReset) {
      onReset(emptyData);
    }
  };

  const handleSearch = debounce(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onSearch) {
        const val = e.target.value;
        if (val.length > 2) {
          searchRef.current?.classList?.add('dropdown__search--loading');
          onSearch(val).then((results) => {
            setNoResults(!results || results.length === 0);
            setSearchValues(
              results.map((r) => ({ ...r, type: 'checkbox' as const })),
            );
            searchRef.current?.classList?.remove('dropdown__search--loading');
          });
        } else {
          searchRef.current?.classList?.remove('dropdown__search--loading');
          setNoResults(false);
          setSearchValues([]);
        }
        return;
      }

      setSearchTerm(e.target.value.toLowerCase());
    },
    300,
    false,
  );

  const handleChange = (update: DataMap) => {
    if (scrollContainer.current) {
      const position = scrollContainer.current.scrollTop;
      if (position !== scrollPosition) setScrollPosition(position);
    }

    const next = { ...(dataState || {}), ...update } as DataMap;
    setData(next);

    if (onChange) {
      onChange(next);
    }
  };

  const handleResultSelect = (update: DataMap) => {
    const add: DropdownItem[] = [];
    searchValues.forEach((item) => {
      if (
        item?.name &&
        item.name in update &&
        Array.isArray(update[item.name]) &&
        (update[item.name] as string[]).includes(item.value as string)
      ) {
        add.push(item);
      }
    });

    setSelectedSearchValues([...add, ...selectedSearchValues]);
    setSearchValues(searchValues.filter((item) => !add.includes(item)));
    handleChange(update);
  };

  const handleResultDeselect = (update: DataMap) => {
    const add: DropdownItem[] = [];
    setSelectedSearchValues(
      selectedSearchValues.filter((item) => {
        if (
          item?.name &&
          item.name in update &&
          Array.isArray(update[item.name]) &&
          !(update[item.name] as string[]).includes(item.value as string)
        ) {
          add.push(item);
          return false;
        }
        return true;
      }),
    );
    setSearchValues([...add, ...searchValues]);
    handleChange(update);
  };

  const handleShowMore = () => {
    setShowMore(!doShowMore);
  };

  const summary = (
    <>
      {label}
      {showActiveCount && selectedCount > 0 && (
        <span className="dropdown__count"> ({selectedCount})</span>
      )}
    </>
  );

  if (!name) {
    name = 'dropdown-' + uuidv4();
  }

  const dropdownContent = (
    <div className="dropdown__content" ref={content}>
      {search && (
        <div ref={searchRef} className="dropdown__search">
          <input
            className="dropdown__search-input"
            type="search"
            placeholder={search}
            onChange={handleSearch as any}
          />
        </div>
      )}
      <ul className="dropdown__values" ref={scrollContainer}>
        {selectedSearchValues.length > 0 && (
          <DropdownValues
            values={[
              {
                label: searchSelected || '',
                values: selectedSearchValues,
              } as DropdownItem,
            ]}
            data={dataState}
            onChange={handleResultDeselect}
            disabled={disabled}
          />
        )}
        {selectedSearchValues.length > 3 && (
          <li className="dropdown__showmore">
            <Action
              label={showMore || ''}
              icon="add"
              onClick={handleShowMore}
            />
          </li>
        )}
        {noResults && (
          <li className="dropdown__noresults">{searchNoResults}</li>
        )}
        {searchValues.length > 0 && (
          <DropdownValues
            values={[
              {
                label: searchResults || '',
                values: searchValues,
              } as DropdownItem,
            ]}
            data={dataState}
            onChange={handleResultSelect}
            disabled={disabled}
          />
        )}
        <DropdownValues
          values={values as DropdownItem[]}
          data={dataState}
          searchTerm={searchTerm}
          disabled={disabled}
          onChange={handleChange}
        />
      </ul>
      <div className="dropdown__actions">
        {reset && (
          <Button
            type="tertiary"
            size="small"
            discreet={true}
            onClick={handleReset}
            label={reset}
          />
        )}
        {actionLabel && (
          <Button
            type={isDirty ? 'primary' : 'secondary'}
            submit={isDirty}
            size="small"
            onClick={handleApply}
            label={actionLabel}
          />
        )}
      </div>
    </div>
  );

  if (inline) {
    return (
      <div
        className={[
          'dropdown',
          'dropdown--inline',
          showActiveCount && selectedCount > 0 ? 'dropdown--active' : '',
          disabled ? 'dropdown--disabled' : '',
          className || '',
        ].join(' ')}
      >
        {dropdownContent}
      </div>
    );
  }

  return (
    <AnimatedDetails
      summary={summary}
      name={name}
      title={title}
      ref={ref}
      onKeyDown={handleKeyDown}
      open={isOpen ?? undefined}
      onToggle={(state: boolean) => {
        if (state !== isOpen) {
          setOpen(state);
          if (!state) {
            applyIfDirtyRef.current();
          }
        }
      }}
      duration={150}
      className={[
        'dropdown',
        showActiveCount && selectedCount > 0 ? 'dropdown--active' : '',
        disabled ? 'dropdown--disabled' : '',
        selectedSearchValues.length > 0 ? 'dropdown--has-selected-values' : '',
        doShowMore ? 'dropdown--show-more' : '',
        className || '',
      ].join(' ')}
      summaryClassName="dropdown__label"
      contentClassName="dropdown__wrapper"
      contentRef={wrapper as any}
    >
      {dropdownContent}
    </AnimatedDetails>
  );
};
